import React, { useState, useEffect, useCallback } from 'react';
import HubCorrespondence from '../Calendar/HubCorrespondence';
import ScoreInput from '../../components/risk/ScoreInput';
import AIAssistPanel from '../../components/risk/AIAssistPanel';
import { UPDATE_KINDS, KIND_CLS, updateKindLabel, kindOf } from './updateKinds';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

const STATUS_OPTS = [
  ['open', 'Open'], ['monitoring', 'Monitoring'], ['mitigating', 'Mitigating'],
  ['accepted', 'Accepted'], ['transferred', 'Transferred'], ['closed', 'Closed'],
];
const STATUS_TONE = {
  open: 'bg-blue-100 text-blue-700', monitoring: 'bg-amber-100 text-amber-800',
  mitigating: 'bg-amber-100 text-amber-700', accepted: 'bg-slate-100 text-slate-700',
  transferred: 'bg-slate-100 text-slate-700', closed: 'bg-green-100 text-green-700',
};
const TREATMENTS = [['mitigate', 'Mitigate'], ['accept', 'Accept'], ['transfer', 'Transfer'], ['avoid', 'Avoid']];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

// Methodology-aware score from the stored register fields (mirrors ScoreInput).
function computeScore(methodology, r, rpnScale = 10) {
  if (methodology === 'rpn_fmea') {
    const s = (r.severity_score || 0) * (r.occurrence_score || 0) * (r.detection_score || 0);
    const max = rpnScale * rpnScale * rpnScale;
    let band = 'Low', color = '#16a34a';
    if (s > 0.5 * max) { band = 'Critical'; color = '#dc2626'; } else if (s > 0.3 * max) { band = 'High'; color = '#f97316'; } else if (s > 0.1 * max) { band = 'Moderate'; color = '#eab308'; }
    return { score: s, max, band, color, label: 'RPN' };
  }
  if (methodology === 'inherent_residual') {
    const inh = (r.severity_score || 0) * (r.likelihood_score || 0) * (r.exposure_score || 0);
    const ca = ((Number(r.control_design_score) || 0) + (Number(r.control_operating_score) || 0)) / 2;
    const res = Math.max(inh * (1 - Math.min(ca / 4, 1)), 1);
    let tier = 'C', color = '#16a34a';
    if (res >= 50) { tier = 'A'; color = '#dc2626'; } else if (res >= 30) { tier = 'B'; color = '#d97706'; }
    return { score: Math.round(res * 100) / 100, max: 125, band: `Tier ${tier}`, color, label: 'Residual', inherent: inh };
  }
  const imp = (r.impact_score || r.severity_score || 0) * (r.likelihood_score || 0);
  let band = 'Low', color = '#16a34a';
  if (imp >= 16) { band = 'Critical'; color = '#dc2626'; } else if (imp >= 10) { band = 'High'; color = '#f97316'; } else if (imp >= 5) { band = 'Moderate'; color = '#eab308'; }
  return { score: imp, max: 25, band, color, label: 'Score' };
}

export default function RiskDetail({ riskId, onBack, onOpenRisk }) {
  const [risk, setRisk] = useState(null);
  const [methodology, setMethodology] = useState('likelihood_impact_5x5');
  const [rpnScale, setRpnScale] = useState(10);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [scores, setScores] = useState(null);
  const [controls, setControls] = useState('');
  const [treatment, setTreatment] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [receivedDate, setReceivedDate] = useState('');
  const [closedDate, setClosedDate] = useState('');
  const [monthly, setMonthly] = useState(false);
  const [savingDates, setSavingDates] = useState(false);
  const [ownerEdit, setOwnerEdit] = useState(false);
  const [ownerIds, setOwnerIds] = useState([]);
  const [orgUsers, setOrgUsers] = useState(null);
  const [savingOwners, setSavingOwners] = useState(false);
  const [deptEdit, setDeptEdit] = useState(false);
  const [deptIds, setDeptIds] = useState([]);
  const [orgDepts, setOrgDepts] = useState(null);
  const [savingDepts, setSavingDepts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [updateKind, setUpdateKind] = useState('note');
  const [posting, setPosting] = useState(false);
  const [addingChild, setAddingChild] = useState(false);
  const [childTitle, setChildTitle] = useState('');
  const [childDesc, setChildDesc] = useState('');
  const [creatingChild, setCreatingChild] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/risks/${riskId}`, { credentials: 'include' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load risk');
      const r = data.risk;
      setRisk(r);
      setMethodology(data.methodology || 'likelihood_impact_5x5');
      setRpnScale(Number(data.rpnScale) || 10);
      setUpdates(r.updates || []);
      setScores({
        impact: r.impact_score || r.severity_score || 3,
        severity: r.severity_score || 5,
        likelihood: r.likelihood_score || 3,
        occurrence: r.occurrence_score || 5,
        detection: r.detection_score || 5,
        exposure: r.exposure_score || 3,
        control_design: Number(r.control_design_score) || 0,
        control_operating: Number(r.control_operating_score) || 0,
      });
      setControls(r.current_controls || '');
      setTreatment(r.treatment || '');
      setReviewDate(r.review_date ? String(r.review_date).slice(0, 10) : '');
      setReceivedDate(r.received_date ? String(r.received_date).slice(0, 10) : '');
      setClosedDate(r.closed_at ? String(r.closed_at).slice(0, 10) : '');
      setMonthly(r.monthly_update_required === true);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, [riskId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 text-slate-500">Loading risk…</div>;
  if (err) return <div className="p-8 text-red-600">{err} <button onClick={onBack} className="underline ml-2">Back</button></div>;
  if (!risk || !scores) return null;

  // Umbrella: a general risk whose level is the worst (highest-scoring) sub-risk.
  const isUmbrella = !!risk.is_umbrella;
  const children = risk.children || [];
  const childResults = children.map(c => ({ c, r: computeScore(methodology, c, rpnScale) }));
  const worst = childResults.reduce((b, x) => (!b || (Number(x.r.score) || 0) > (Number(b.r.score) || 0)) ? x : b, null);
  const result = isUmbrella
    ? (worst ? worst.r : { score: '—', max: '—', band: 'No sub-risks yet', color: '#94a3b8', label: 'Level' })
    : computeScore(methodology, risk, rpnScale);

  const saveFields = async () => {
    setSaving(true);
    try {
      const body = {
        severity_score: methodology === 'likelihood_impact_5x5' ? scores.impact : scores.severity,
        likelihood_score: scores.likelihood,
        exposure_score: scores.exposure,
        occurrence_score: scores.occurrence,
        detection_score: scores.detection,
        impact_score: scores.impact,
        control_design_score: scores.control_design,
        control_operating_score: scores.control_operating,
        current_controls: controls,
        treatment: treatment || null,
        review_date: reviewDate || null,
      };
      const res = await fetch(`${API_BASE}/risk-assessment/risks/${riskId}`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed');
      await load();
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  const saveDates = async () => {
    setSavingDates(true);
    try {
      const body = { received_date: receivedDate || null };
      if (risk.status === 'closed') body.closed_at = closedDate || null;
      const res = await fetch(`${API_BASE}/risk-assessment/risks/${riskId}`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed');
      await load();
    } catch (e) { alert(e.message); } finally { setSavingDates(false); }
  };

  // ── Owner (re)assignment — compliance assigns the accountable risk owner
  // at the risk level. PATCH owner_user_ids; the backend keeps the primary
  // owner_user_id in sync. Workstream update requests go to this owner.
  const startOwnerEdit = async () => {
    setOwnerIds(Array.isArray(risk.owner_user_ids) ? risk.owner_user_ids.filter(Boolean) : (risk.owner_user_id ? [risk.owner_user_id] : []));
    setOwnerEdit(true);
    if (!orgUsers) {
      try {
        const res = await fetch(`${API_BASE}/risk-intake/users`, { credentials: 'include' });
        const data = await res.json();
        setOrgUsers(data.users || []);
      } catch { setOrgUsers([]); }
    }
  };

  const saveOwners = async () => {
    setSavingOwners(true);
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/risks/${riskId}`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner_user_ids: ownerIds }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed');
      setOwnerEdit(false);
      await load();
    } catch (e) { alert(e.message); } finally { setSavingOwners(false); }
  };

  // A risk can sit with more than one department, same as it can have more
  // than one owner. PATCH department_ids; the backend keeps the primary
  // department_id in sync.
  const startDeptEdit = async () => {
    setDeptIds(Array.isArray(risk.department_ids) ? risk.department_ids.filter(Boolean) : (risk.department_id ? [risk.department_id] : []));
    setDeptEdit(true);
    if (!orgDepts) {
      try {
        const res = await fetch(`${API_BASE}/departments`, { credentials: 'include' });
        const data = await res.json();
        setOrgDepts(data.departments || data || []);
      } catch { setOrgDepts([]); }
    }
  };

  const saveDepts = async () => {
    setSavingDepts(true);
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/risks/${riskId}`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ department_ids: deptIds }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed');
      setDeptEdit(false);
      await load();
    } catch (e) { alert(e.message); } finally { setSavingDepts(false); }
  };

  const toggleMonthly = async () => {
    const next = !monthly;
    setMonthly(next);
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/risks/${riskId}`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ monthly_update_required: next }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed');
      await load();
    } catch (e) { setMonthly(!next); alert(e.message); }
  };

  const createChild = async (title, description) => {
    const res = await fetch(`${API_BASE}/risk-assessment/risks`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ risk_title: title, risk_description: description || null, parent_risk_id: riskId }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Failed to add sub-risk');
    await load();
  };

  const addSubRisk = async () => {
    if (!childTitle.trim()) return;
    setCreatingChild(true);
    try {
      await createChild(childTitle.trim(), childDesc.trim() || null);
      setChildTitle(''); setChildDesc(''); setAddingChild(false);
    } catch (e) { alert(e.message); } finally { setCreatingChild(false); }
  };

  // Agent-suggested sub-risk (reads the parent + existing siblings as context).
  const acceptSuggestedChild = async (s) => {
    if (!s?.risk_title) return;
    setCreatingChild(true);
    try { await createChild(s.risk_title, s.risk_description || null); }
    catch (e) { alert(e.message); } finally { setCreatingChild(false); }
  };

  /**
   * Close the risk outright.
   *
   * Same endpoint the status dropdown uses, so the server-side effect is
   * identical — status closed, closed_at/closed_by stamped, active false, an
   * entry on the timeline and a row in the audit log. This just makes it a
   * findable action instead of an option buried in a dropdown.
   *
   * Any note already typed is carried through as the closing rationale, which
   * is what an auditor will look for.
   */
  const closeRisk = async () => {
    if (!window.confirm('Close this risk?\n\nIt will be removed from the active register. It stays on the record and can be found again using the Closed filter.')) return;
    setPosting(true);
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/risks/${riskId}/updates`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.trim() || 'Risk closed.', status_to: 'closed' }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Could not close the risk');
      setNote(''); setNewStatus(''); await load();
    } catch (e) { alert(e.message); } finally { setPosting(false); }
  };

  const postUpdate = async () => {
    if (!note.trim() && !newStatus) return;
    setPosting(true);
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/risks/${riskId}/updates`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note || null, status_to: newStatus || null, update_kind: updateKind }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Update failed');
      setNote(''); setNewStatus(''); setUpdateKind('note'); await load();
    } catch (e) { alert(e.message); } finally { setPosting(false); }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 mb-4">← Back to register</button>

        {/* Header */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_TONE[risk.status] || STATUS_TONE.open}`}>
                  {STATUS_OPTS.find((s) => s[0] === risk.status)?.[1] || risk.status || 'Open'}
                </span>
                {risk.domain_name && <span className="text-xs text-slate-400">{risk.domain_name}</span>}
                {isUmbrella && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">Umbrella · {children.length} sub-risks</span>}
                {risk.parent && <button onClick={() => onOpenRisk && onOpenRisk(risk.parent.id)} className="text-xs text-indigo-600 hover:text-indigo-700 underline">↳ Part of: {risk.parent.risk_title}</button>}
              </div>
              <h1 className="text-xl font-bold text-slate-900">{risk.risk_title}</h1>
              <p className="text-sm text-slate-600 mt-1 max-w-3xl">{risk.risk_description}</p>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-400">{result.label}</div>
              <div className="text-3xl font-bold" style={{ color: result.color }}>{result.score}<span className="text-sm font-normal text-slate-400"> / {result.max}</span></div>
              <span className="inline-block mt-1 px-3 py-1 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: result.color }}>{result.band}</span>
              {methodology === 'inherent_residual' && result.inherent > 0 && <div className="text-[11px] text-slate-400 mt-1">Inherent: {result.inherent}</div>}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: scoring + controls */}
          <div className="space-y-6">
            {isUmbrella ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-slate-900">Sub-risks ({children.length})</h2>
                  <button onClick={() => setAddingChild(true)} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">+ Add sub-risk</button>
                </div>
                <p className="text-xs text-slate-500">A general (umbrella) risk. Its level is the worst of its sub-risks{worst ? `: "${worst.c.risk_title}"` : ''}. Open a sub-risk to score it, assign owners, add controls, and track its own updates.</p>
                {childResults.length === 0 && !addingChild && <p className="text-sm text-slate-400">No sub-risks yet. Add one to break this risk down.</p>}
                <div className="divide-y divide-slate-100">
                  {childResults.map(({ c, r }) => (
                    <button key={c.id} onClick={() => onOpenRisk && onOpenRisk(c.id)} className="w-full text-left py-3 px-2 flex items-center justify-between hover:bg-slate-50 rounded-lg">
                      <div className="min-w-0 pr-3">
                        <div className="text-sm font-medium text-slate-800 truncate">{c.risk_title}</div>
                        <div className="text-xs text-slate-400">{STATUS_OPTS.find((s) => s[0] === c.status)?.[1] || c.status || 'Open'} · Owner: {c.owner_name || '—'}</div>
                      </div>
                      <span className="ml-2 px-2.5 py-1 rounded-lg text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: r.color }}>{r.band}</span>
                    </button>
                  ))}
                </div>
                {addingChild && (
                  <div className="border border-slate-200 rounded-xl p-3 space-y-2">
                    <input type="text" value={childTitle} onChange={(e) => setChildTitle(e.target.value)} placeholder="Sub-risk title" autoFocus className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                    <textarea value={childDesc} onChange={(e) => setChildDesc(e.target.value)} rows={2} placeholder="Sub-risk description" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none" />
                    <div className="flex justify-end gap-2">
                      <button onClick={() => { setAddingChild(false); setChildTitle(''); setChildDesc(''); }} className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-lg">Cancel</button>
                      <button onClick={addSubRisk} disabled={creatingChild || !childTitle.trim()} className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg disabled:opacity-50">{creatingChild ? 'Adding…' : 'Add'}</button>
                    </div>
                  </div>
                )}
                <AIAssistPanel
                  mode="risks"
                  context={{
                    department: (risk.department_names?.length ? risk.department_names.join(', ') : risk.department_name) || undefined,
                    domain: risk.domain_name || undefined,
                    parentRisk: { title: risk.risk_title, description: risk.risk_description },
                    siblings: children.map((c) => c.risk_title)
                  }}
                  onAcceptRisk={acceptSuggestedChild}
                />
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4">Score</h2>
                <ScoreInput
                  methodology={methodology}
                  rpnScale={rpnScale}
                  values={scores}
                  onChange={(field, value) => setScores((s) => ({ ...s, [field]: value }))}
                />
              </div>
            )}

            {!isUmbrella && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
              <h2 className="font-semibold text-slate-900">Controls &amp; treatment</h2>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Current controls</label>
                <textarea value={controls} onChange={(e) => setControls(e.target.value)} rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 resize-none text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Treatment</label>
                  <select value={treatment} onChange={(e) => setTreatment(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm bg-white">
                    <option value="">Not set</option>
                    {TREATMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Review by</label>
                  <input type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm" />
                </div>
              </div>
              <button onClick={saveFields} disabled={saving} className="px-5 py-2.5 bg-gradient-to-r from-orange-600 to-amber-500 text-white rounded-xl hover:opacity-90 disabled:opacity-50 text-sm font-semibold">
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
            )}
          </div>

          {/* Right: lifecycle + updates */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
              <h2 className="font-semibold text-slate-900">Status &amp; updates</h2>
              <p className="text-xs text-slate-500">Add an update and say what kind it is: the quarterly update, an email you sent or received, a meeting. Change the status or close the risk. Every entry is kept on the timeline for the audit trail.</p>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add an update (what changed, a decision, progress)…"
                className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 resize-none text-sm" />
              <div className="flex items-center gap-2 flex-wrap">
                <select value={updateKind} onChange={(e) => setUpdateKind(e.target.value)} title="What kind of update this is" className="px-3 py-2 border border-slate-300 rounded-xl text-sm bg-white">
                  {UPDATE_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-xl text-sm bg-white">
                  <option value="">Keep status ({STATUS_OPTS.find((s) => s[0] === risk.status)?.[1] || risk.status})</option>
                  {STATUS_OPTS.filter(([v]) => v !== risk.status).map(([v, l]) => <option key={v} value={v}>Change to {l}</option>)}
                </select>
                <button onClick={postUpdate} disabled={posting || (!note.trim() && !newStatus)} className="ml-auto px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 disabled:opacity-50 text-sm font-medium">
                  {posting ? 'Saving…' : 'Add update'}
                </button>
              </div>

              {/* Closing was only reachable as "Change to Closed" inside a
                  dropdown labelled "Keep status", which is why nobody could
                  find it. It is the one lifecycle action with a real
                  consequence — the risk leaves the register — so it gets its
                  own button, and a confirmation that says where the risk goes. */}
              {risk.status !== 'closed' && (
                <div className="pt-3 border-t border-slate-100">
                  <button
                    onClick={closeRisk}
                    disabled={posting}
                    title="Close this risk. It leaves the active register and stays searchable under Closed."
                    className="w-full px-4 py-2 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 disabled:opacity-50 text-sm font-medium"
                  >
                    {posting ? 'Closing…' : 'Close this risk'}
                  </button>
                  <p className="text-xs text-slate-400 mt-1.5">
                    Removes it from the active register. It stays on the record and can be found
                    again with the <span className="font-medium">Closed</span> filter.
                  </p>
                </div>
              )}

              <div className="pt-2 space-y-3">
                {updates.length === 0 && <p className="text-sm text-slate-400">No updates yet.</p>}
                {(() => {
                  // Timeline grouped by quarter (UTC, matching how Workstream
                  // stamps entries), with a source badge on entries that came
                  // in through a follow-up — respond page or email reply.
                  const qOf = (d) => { const t = new Date(d); return `Q${Math.floor(t.getUTCMonth() / 3) + 1} ${t.getUTCFullYear()}`; };
                  let lastQ = null;
                  return updates.map((u) => {
                    const q = u.created_at ? qOf(u.created_at) : null;
                    const header = q && q !== lastQ;
                    lastQ = q || lastQ;
                    const kind = kindOf(u);
                    return (
                      <React.Fragment key={u.id}>
                        {header && <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide pt-1">{q}</div>}
                        <div className="flex gap-3 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 flex-shrink-0" />
                          <div className="min-w-0">
                            {u.update_type === 'status_change' && (
                              <p className="text-slate-700"><span className="font-medium">Status:</span> {u.status_from} → {u.status_to}</p>
                            )}
                            {u.note && <p className="text-slate-700">{u.note}</p>}
                            <p className="text-xs text-slate-400">
                              {u.created_by_name ? `${u.created_by_name} · ` : ''}{fmtDateTime(u.created_at)}
                              {kind !== 'note' && <span className={`ml-2 px-1.5 py-0.5 rounded font-medium ${KIND_CLS[kind] || 'bg-slate-100 text-slate-600'}`}>{updateKindLabel(kind)}</span>}
                            </p>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  });
                })()}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm">
              <h2 className="font-semibold text-slate-900 mb-3">Details</h2>
              <dl className="space-y-1.5">
                <div className="flex justify-between gap-3 items-start">
                  <dt className="text-slate-400">Department(s)</dt>
                  <dd className="text-slate-700 text-right">
                    {!deptEdit ? (
                      <span>
                        {risk.department_names?.length ? risk.department_names.join(', ') : (risk.department_name || '—')}
                        <button onClick={startDeptEdit} className="ml-2 text-xs text-orange-700 hover:text-orange-800 underline underline-offset-2">Edit</button>
                      </span>
                    ) : (
                      <div className="text-left w-56">
                        <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1 bg-white">
                          {orgDepts === null ? <div className="text-xs text-slate-400">Loading departments…</div> :
                            orgDepts.length === 0 ? <div className="text-xs text-slate-400">No departments found.</div> :
                            orgDepts.map((d) => (
                              <label key={d.id} className="flex items-center gap-2 text-xs cursor-pointer">
                                <input type="checkbox" className="accent-orange-600"
                                  checked={deptIds.includes(d.id)}
                                  onChange={(e) => setDeptIds(e.target.checked ? [...deptIds, d.id] : deptIds.filter((x) => x !== d.id))} />
                                <span className="truncate">{d.name}</span>
                              </label>
                            ))}
                        </div>
                        <div className="mt-1.5 flex gap-2 justify-end">
                          <button onClick={() => setDeptEdit(false)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                          <button onClick={saveDepts} disabled={savingDepts} className="text-xs px-2.5 py-1 bg-slate-900 text-white rounded-lg disabled:opacity-50">{savingDepts ? 'Saving…' : 'Save departments'}</button>
                        </div>
                      </div>
                    )}
                  </dd>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <dt className="text-slate-400">Owner(s)</dt>
                  <dd className="text-slate-700 text-right">
                    {!ownerEdit ? (
                      <span>
                        {risk.owner_names?.length ? risk.owner_names.join(', ') : (risk.owner_name || '—')}
                        <button onClick={startOwnerEdit} className="ml-2 text-xs text-orange-700 hover:text-orange-800 underline underline-offset-2">Edit</button>
                      </span>
                    ) : (
                      <div className="text-left w-56">
                        <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1 bg-white">
                          {orgUsers === null ? <div className="text-xs text-slate-400">Loading users…</div> :
                            orgUsers.map((u) => (
                              <label key={u.id} className="flex items-center gap-2 text-xs cursor-pointer">
                                <input type="checkbox" className="accent-orange-600"
                                  checked={ownerIds.includes(u.id)}
                                  onChange={(e) => setOwnerIds(e.target.checked ? [...ownerIds, u.id] : ownerIds.filter((x) => x !== u.id))} />
                                <span className="truncate">{u.name || u.email}</span>
                              </label>
                            ))}
                        </div>
                        <div className="mt-1.5 flex gap-2 justify-end">
                          <button onClick={() => setOwnerEdit(false)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                          <button onClick={saveOwners} disabled={savingOwners} className="text-xs px-2.5 py-1 bg-slate-900 text-white rounded-lg disabled:opacity-50">{savingOwners ? 'Saving…' : 'Save owners'}</button>
                        </div>
                      </div>
                    )}
                  </dd>
                </div>
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Domain</dt><dd className="text-slate-700 text-right">{risk.domain_name || '—'}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Type</dt><dd className="text-slate-700 text-right">{risk.risk_type || '—'}</dd></div>
                <div className="flex justify-between gap-3 items-center"><dt className="text-slate-400">Monthly updates</dt><dd><label className="inline-flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={monthly} onChange={toggleMonthly} className="w-4 h-4 accent-orange-600 rounded" /><span className="text-slate-700">{monthly ? 'Required' : 'Off'}</span></label></dd></div>
                <div className="flex justify-between gap-3 items-center"><dt className="text-slate-400">Date received</dt><dd><input type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} className="px-2 py-1 border border-slate-300 rounded-lg text-xs" /></dd></div>
                {risk.status === 'closed' && <div className="flex justify-between gap-3 items-center"><dt className="text-slate-400">Closed date</dt><dd><input type="date" value={closedDate} onChange={(e) => setClosedDate(e.target.value)} className="px-2 py-1 border border-slate-300 rounded-lg text-xs" /></dd></div>}
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Review by</dt><dd className="text-slate-700 text-right">{fmtDate(risk.review_date)}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Created</dt><dd className="text-slate-700 text-right">{fmtDate(risk.created_at)}</dd></div>
              </dl>
              <div className="mt-3 flex items-center gap-3">
                <button onClick={saveDates} disabled={savingDates} className="px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 disabled:opacity-50 text-xs font-medium">{savingDates ? 'Saving…' : 'Save dates'}</button>
                <span className="text-[11px] text-slate-400">Update requests are sent from Workstream; the owner's reply lands on this timeline.</span>
              </div>
              <details className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                <summary className="text-xs font-semibold text-slate-700 cursor-pointer select-none">Correspondence — this risk only</summary>
                <div className="mt-3">
                  <HubCorrespondence base="/api/risk-assessment/correspondence" item={riskId} chipFor={() => 'Risk'}
                    intro="Every email sent from Workstream about THIS risk, with its follow-ups and replies. Reply here and it goes out the same way, into the same thread." />
                </div>
              </details>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
